import { useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { CarrierPicker } from '../../../../../components/CarrierPicker'
import { CacheKeys } from '../../../../../lib/cache'
import { toSnakeCase } from '../../../../../lib/typeHelper'
import { formatCurrency } from '../../../../../lib/utils'
import { CoverageType } from '../../../../../models/insuranceCoverage'
import { InsuranceQuote } from '../../../../../models/insuranceQuote'
import type { InsuranceCoverage, InsuranceOneTimeFee } from '../../../../../models/insuranceTypes'
import { serializeTerm } from './insuranceFormShared'

const COVERAGE_TYPE_OPTIONS: { value: CoverageType; label: string }[] = [
  { value: CoverageType.AUTO_LIABILITY, label: 'Auto Liability' },
  { value: CoverageType.AUTO_PHYSICAL_DAMAGE, label: 'Physical Damage' },
  { value: CoverageType.GENERAL_LIABILITY, label: 'General Liability' },
  { value: CoverageType.MOTOR_TRUCK_CARGO, label: 'Cargo' },
  { value: CoverageType.WORKERS_COMPENSATION, label: 'Workers Comp' },
  { value: CoverageType.PET, label: 'Pet insurance' },
  { value: CoverageType.TOOLS_AND_EQUIPMENT, label: 'Tools and Equipment' },
]

interface CoverageFormData {
  id?: string
  coverageType: string
  vendorCoverageType: string
  premium: number | null
  limit: number | null
  deductible: number | null
  comprehensiveDeductible: number | null
  premiumPerUnit: number | null
  label: string
  isNew?: boolean
}

interface FeeFormData {
  id?: string
  label: string
  amount: string
  isNew?: boolean
}

function feeToFormData(fee: InsuranceOneTimeFee): FeeFormData {
  return { id: fee.id, label: fee.label, amount: String(fee.amount) }
}

interface EditQuoteDialogProps {
  quote: InsuranceQuote
  orgId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

function coverageToFormData(coverage: InsuranceCoverage): CoverageFormData {
  return {
    id: coverage.id,
    coverageType: coverage.coverageType,
    vendorCoverageType: coverage.vendorCoverageType,
    premium: coverage.premium,
    limit: coverage.limit,
    deductible: coverage.deductible,
    comprehensiveDeductible: coverage.comprehensiveDeductible ?? null,
    premiumPerUnit: coverage.premiumPerUnit,
    label: coverage.label,
  }
}

export function EditQuoteDialog({ quote, orgId, open, onOpenChange }: EditQuoteDialogProps) {
  const queryClient = useQueryClient()

  const [oneTimeFees, setOneTimeFees] = useState<FeeFormData[]>(quote.oneTimeFees.map(feeToFormData))
  const [deletedOneTimeFeeIds, setDeletedOneTimeFeeIds] = useState<string[]>([])
  const [coverages, setCoverages] = useState<CoverageFormData[]>(quote.coverages.map(coverageToFormData))
  const [deletedCoverageIds, setDeletedCoverageIds] = useState<string[]>([])
  const [carrierId, setCarrierId] = useState<string | null>(quote.carrierId ?? null)
  const [brokerNote, setBrokerNote] = useState(quote.brokerNote)
  const [brokerRecommended, setBrokerRecommended] = useState(quote.brokerRecommended)

  const feesTotal = oneTimeFees.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0)
  const coveragePremiumTotal = coverages.reduce(
    (sum, c) => sum + (parseFloat(String(c.premium ?? 0)) || 0),
    0
  )
  const newPremium = feesTotal + coveragePremiumTotal

  const mutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      axios
        .patch(`/api/v1/insure/organisations/${orgId}/quotes/${quote.id}/`, toSnakeCase(data))
        .then((res) => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKeys.ORGANISATIONS, orgId, 'application'] })
      onOpenChange(false)
    },
  })

  function handleSave() {
    const payload = {
      carrierId,
      brokerNote,
      brokerRecommended,
      oneTimeFees: oneTimeFees.map((f) => ({
        id: f.isNew ? undefined : f.id,
        label: f.label,
        amount: parseFloat(f.amount) || 0,
      })),
      deletedOneTimeFeeIds,
      coverages: coverages.map((c) => ({
        id: c.isNew ? undefined : c.id,
        coverageType: c.coverageType,
        vendorCoverageType: c.vendorCoverageType,
        premium: serializeTerm(c.premium, false),
        limit: serializeTerm(c.limit),
        deductible: serializeTerm(c.deductible),
        comprehensiveDeductible: serializeTerm(c.comprehensiveDeductible),
        premiumPerUnit: serializeTerm(c.premiumPerUnit, false),
      })),
      deletedCoverageIds,
    }
    mutation.mutate(payload)
  }

  function updateFee(index: number, field: 'label' | 'amount', value: string) {
    setOneTimeFees((prev) => prev.map((f, i) => (i === index ? { ...f, [field]: value } : f)))
  }

  function addFee() {
    setOneTimeFees((prev) => [...prev, { isNew: true, label: '', amount: '0' }])
  }

  function removeFee(index: number) {
    const fee = oneTimeFees[index]
    if (fee.id && !fee.isNew) {
      setDeletedOneTimeFeeIds((prev) => [...prev, fee.id!])
    }
    setOneTimeFees((prev) => prev.filter((_, i) => i !== index))
  }

  function updateCoverage(index: number, field: keyof CoverageFormData, value: string | number | null) {
    setCoverages((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c
        const next = { ...c, [field]: value }
        // Its input is hidden off physical damage, so a leftover value would be saved.
        return next.coverageType === CoverageType.AUTO_PHYSICAL_DAMAGE
          ? next
          : { ...next, comprehensiveDeductible: null }
      })
    )
  }

  function addCoverage() {
    setCoverages((prev) => [
      ...prev,
      {
        isNew: true,
        coverageType: CoverageType.AUTO_LIABILITY,
        vendorCoverageType: '',
        premium: null,
        limit: null,
        deductible: null,
        comprehensiveDeductible: null,
        premiumPerUnit: null,
        label: 'Auto Liability',
      },
    ])
  }

  function removeCoverage(index: number) {
    const coverage = coverages[index]
    if (coverage.id && !coverage.isNew) {
      setDeletedCoverageIds((prev) => [...prev, coverage.id!])
    }
    setCoverages((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Edit Quote - {quote.vendorName}</DialogTitle>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1">
          <div>
            <Label className="text-muted-foreground text-xs">Carrier (optional)</Label>
            <div className="mt-1">
              <CarrierPicker value={carrierId} onChange={setCarrierId} />
            </div>
          </div>

          <div>
            <Label className="text-muted-foreground text-xs">Broker note (optional)</Label>
            <div className="mt-1">
              <Textarea
                value={brokerNote}
                onChange={(e) => setBrokerNote(e.target.value)}
                placeholder='Shown to the customer on their Quotes Page under "Notes from <broker>".'
              />
            </div>

            <Label className="mt-3 flex w-fit cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={brokerRecommended}
                onCheckedChange={(checked) => setBrokerRecommended(checked === true)}
              />
              Broker recommended
            </Label>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
              <span className="text-muted-foreground text-sm">Current premium</span>
              <span className="font-semibold">{formatCurrency(quote.premium)}</span>
            </div>
            <div className="flex items-center justify-between rounded-md bg-blue-50 px-3 py-2">
              <span className="text-muted-foreground text-sm">New premium</span>
              <span className="font-semibold">{formatCurrency(newPremium)}</span>
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">One-time fees</h3>
              <Button type="button" variant="outline" size="sm" onClick={addFee}>
                <Plus className="mr-1 h-4 w-4" />
                Add fee
              </Button>
            </div>

            <div className="space-y-3">
              {oneTimeFees.map((fee, index) => (
                <div
                  key={fee.id ?? `new-fee-${index}`}
                  className="grid grid-cols-[1fr_1fr_auto] items-end gap-3"
                >
                  <div>
                    <Label className="text-muted-foreground text-xs">Label</Label>
                    <Input
                      value={fee.label}
                      onChange={(e) => updateFee(index, 'label', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-muted-foreground text-xs">Amount</Label>
                    <div className="mt-1">
                      <CurrencyInput
                        value={fee.amount === '' ? null : Number(fee.amount)}
                        onChange={(v) => updateFee(index, 'amount', v == null ? '' : String(v))}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => removeFee(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {oneTimeFees.length === 0 && (
                <p className="text-muted-foreground py-2 text-center text-sm">
                  No one-time fees. Click &quot;Add fee&quot; to add one.
                </p>
              )}
            </div>
          </div>

          <div>
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Coverages</h3>
              <Button type="button" variant="outline" size="sm" onClick={addCoverage}>
                <Plus className="mr-1 h-4 w-4" />
                Add coverage
              </Button>
            </div>

            <div className="space-y-4">
              {coverages.map((coverage, index) => (
                <div key={coverage.id ?? `new-${index}`} className="rounded-md border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <Select
                      value={coverage.coverageType}
                      onValueChange={(value) => {
                        const type = value as CoverageType
                        const label = COVERAGE_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? type
                        updateCoverage(index, 'coverageType', type)
                        updateCoverage(index, 'label', label)
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COVERAGE_TYPE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeCoverage(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div>
                      <Label className="text-muted-foreground text-xs">Premium</Label>
                      <div className="mt-1">
                        <CurrencyInput
                          value={coverage.premium}
                          onChange={(v) => updateCoverage(index, 'premium', v)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">Limit</Label>
                      <div className="mt-1">
                        <CurrencyInput
                          value={coverage.limit}
                          onChange={(v) => updateCoverage(index, 'limit', v)}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground text-xs">
                        {coverage.coverageType === CoverageType.AUTO_PHYSICAL_DAMAGE
                          ? 'Collision deductible'
                          : 'Deductible'}
                      </Label>
                      <div className="mt-1">
                        <CurrencyInput
                          value={coverage.deductible}
                          onChange={(v) => updateCoverage(index, 'deductible', v)}
                        />
                      </div>
                    </div>
                    {/* Physical damage is written comprehensive + collision, each with its own. */}
                    {coverage.coverageType === CoverageType.AUTO_PHYSICAL_DAMAGE && (
                      <div>
                        <Label className="text-muted-foreground text-xs">Comprehensive deductible</Label>
                        <div className="mt-1">
                          <CurrencyInput
                            value={coverage.comprehensiveDeductible}
                            onChange={(v) => updateCoverage(index, 'comprehensiveDeductible', v)}
                          />
                        </div>
                      </div>
                    )}
                    <div>
                      <Label className="text-muted-foreground text-xs">Premium/unit</Label>
                      <div className="mt-1">
                        <CurrencyInput
                          value={coverage.premiumPerUnit}
                          onChange={(v) => updateCoverage(index, 'premiumPerUnit', v)}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {coverages.length === 0 && (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  No coverages. Click &quot;Add coverage&quot; to add one.
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={mutation.isPending}
            loading={mutation.isPending ? 'Saving...' : undefined}
          >
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
